import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Box, FormHelperText, Grid, IconButton, Typography } from '@mui/material';
import { Clear as ClearIcon, CloudUpload, Delete } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { moduleService } from '@/services';
import { useNotifications } from '@core/hooks/useNotifications';
import TrolleyShelterLocationImageViewer from './TrolleyShelterLocationImageViewer';
import { DeviceModule } from '@core/types';
import { WcnButton } from '@/components';

interface TrolleyShelterLocationImageEditorProps {
    module: DeviceModule;
    onImageChange: (imagePath: string | null) => void;
    showActions?: boolean;
}

const TrolleyShelterLocationImageEditor: React.FC<TrolleyShelterLocationImageEditorProps> = ({
    module,
    onImageChange,
    showActions = true
}) => {
    const { t } = useTranslation();
    const { notifySuccess, notifyError } = useNotifications();
    const [saving, setSaving] = useState(false);
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [dragActive, setDragActive] = useState(false);
    const [validationError, setValidationError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadingFileName, setUploadingFileName] = useState('');
    const progressIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        return () => clearProgressInterval();
    }, []);

    const ALLOWED_SIZE_BYTES = 512000;
    const ALLOWED_SIZE_KILOBYTES = 512;
    const ALLOWED_TYPES = new Set(['image/jpg', 'image/jpeg', 'image/png', 'image/bmp', 'image/gif']);

    const clearProgressInterval = useCallback(() => {
        if (progressIntervalRef.current !== null) {
            clearInterval(progressIntervalRef.current);
            progressIntervalRef.current = null;
        }
    }, []);

    const validateFile = useCallback(
        (file: File): string | null => {
            if (!ALLOWED_TYPES.has(file.type)) {
                return t('allowedLocationImageTypes');
            }
            if (file.size > ALLOWED_SIZE_BYTES) {
                return t('errors.invalidFileSize', { maxFileSizeInKb: ALLOWED_SIZE_KILOBYTES });
            }
            return null;
        },
        [t]
    );

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            const error = validateFile(file);
            setValidationError(error);
            if (!error) {
                setSelectedFile(file);
                if (!showActions) {
                    uploadLocationImage(file);
                }
            }
        }
    };

    const handleDragOver = (event: React.DragEvent) => {
        event.preventDefault();
        if (!module.imagePath) {
            setDragActive(true);
        }
    };

    const handleDragLeave = () => {
        setDragActive(false);
    };

    const handleDrop = (event: React.DragEvent) => {
        event.preventDefault();
        setDragActive(false);

        if (module.imagePath) return;

        const file = event.dataTransfer.files?.[0];
        if (file) {
            const error = validateFile(file);
            setValidationError(error);
            if (!error) {
                setSelectedFile(file);
                if (!showActions) {
                    uploadLocationImage(file);
                }
            }
        }
    };

    const uploadLocationImage = async (fileToUpload?: File) => {
        const file = fileToUpload ?? selectedFile;
        if (!file) return;

        setSaving(true);
        setIsUploading(true);
        setUploadProgress(0);
        setUploadingFileName(file.name);
        clearProgressInterval();
        progressIntervalRef.current = setInterval(() => {
            setUploadProgress((prev) => {
                if (prev >= 90) {
                    clearProgressInterval();
                    return 90;
                }
                return prev + 6;
            });
        }, 30);

        const formData = new FormData();
        formData.append('locationImage', file);
        formData.append('locationImageName', module.id);

        try {
            const response = await moduleService.createTrolleyShelterLocationImage(formData);
            clearProgressInterval();
            setUploadProgress(100);
            await new Promise((r) => setTimeout(r, 300));
            setIsUploading(false);
            setUploadProgress(0);
            setUploadingFileName('');
            onImageChange(response);
            setSelectedFile(null);
            setValidationError(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            notifySuccess(t('saveModule'));
        } catch (error) {
            clearProgressInterval();
            setIsUploading(false);
            setUploadProgress(0);
            setUploadingFileName('');
            notifyError(t('uploadFailed'));
        } finally {
            setSaving(false);
        }
    };

    const deleteLocationImage = async () => {
        if (!module.imagePath) return;

        setSaving(true);
        try {
            await moduleService.deleteTrolleyShelterLocationImage(module.imagePath as string);
            onImageChange(null);
            notifySuccess(t('saveModule'));
        } catch (error) {
            notifyError(t('deleteFailed'));
        } finally {
            setSaving(false);
        }
    };

    const handleClearFile = () => {
        setSelectedFile(null);
        setValidationError(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <Grid container spacing={2}>
            {!module.imagePath && !isUploading && (
                <Grid item xs={12}>
                    <Box
                        sx={{
                            border: '1px solid',
                            borderColor: dragActive ? 'var(--primary)' : 'var(--sidebar-divider)',
                            borderRadius: '3px',
                            textAlign: 'center',
                            backgroundColor: dragActive ? 'rgba(0,153,204,0.04)' : 'transparent',
                            transition: 'border-color 0.2s ease, background-color 0.2s ease',
                            opacity: module.imagePath ? 0.5 : 1,
                            pointerEvents: module.imagePath ? 'none' : 'auto',
                            px: 3,
                            py: 4,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            cursor: 'pointer',
                            '&:hover': { borderColor: 'var(--primary)' }
                        }}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".jpg,.jpeg,.png,.bmp,.gif"
                            style={{ display: 'none' }}
                            onChange={handleFileSelect}
                            disabled={!!module.imagePath}
                        />
                        <Box
                            sx={{
                                '& i': {
                                    fontSize: '24px !important',
                                    color: `${dragActive ? 'var(--primary)' : 'var(--gray-medium)'} !important`
                                }
                            }}
                        >
                            <i className="bi bi-image" />
                        </Box>
                        {selectedFile ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--gray-medium)' }}>
                                    {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                                </Typography>
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleClearFile();
                                    }}
                                    sx={{
                                        '&:hover': {
                                            backgroundColor: 'transparent'
                                        }
                                    }}
                                >
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        ) : (
                            <>
                                <Typography variant="body2" sx={{ fontWeight: 500, color: 'var(--gray-medium)' }}>
                                    {t('selectOrDragFile')}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'var(--gray-medium)' }}>
                                    [JPG, JPEG, PNG, BMP, GIF, max. {ALLOWED_SIZE_KILOBYTES} KB]
                                </Typography>
                            </>
                        )}
                    </Box>
                    {validationError && (
                        <FormHelperText error sx={{ textAlign: 'center', mt: 1 }}>
                            {validationError}
                        </FormHelperText>
                    )}
                </Grid>
            )}

            {showActions && (
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <WcnButton
                            variant="primary"
                            color="primary"
                            onClick={() => uploadLocationImage()}
                            disabled={!selectedFile || !!validationError || saving || isUploading}
                        >
                            {t('upload')}
                        </WcnButton>
                        <WcnButton
                            variant="red"
                            color="error"
                            onClick={deleteLocationImage}
                            disabled={!module.imagePath || saving || isUploading}
                        >
                            {t('delete')}
                        </WcnButton>
                    </Box>
                </Grid>
            )}

            {(isUploading || module.imagePath) && (
                <Grid item xs={12}>
                    {isUploading ? (
                        <>
                            <Typography
                                variant="caption"
                                sx={{
                                    display: 'block',
                                    mb: 0.5,
                                    ml: 0.25,
                                    color: 'var(--gray-medium)',
                                    fontSize: '12px'
                                }}
                            >
                                {t('uploadingFile')}
                            </Typography>
                            <Box
                                sx={{
                                    position: 'relative',
                                    border: '1px solid var(--sidebar-divider)',
                                    borderRadius: '3px',
                                    height: '40px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 1.5,
                                    overflow: 'hidden'
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontSize: '14px',
                                        fontWeight: 500,
                                        fontFamily: 'var(--font-family-medium)',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        flex: 1
                                    }}
                                >
                                    {uploadingFileName}
                                </Typography>
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        height: '4px',
                                        width: `${uploadProgress}%`,
                                        backgroundColor: 'var(--main-blue)',
                                        borderRadius: '0 0 3px 3px',
                                        transition: 'width 0.1s linear'
                                    }}
                                />
                            </Box>
                        </>
                    ) : (
                        <>
                            {!showActions && module.imagePath && (
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                                    <IconButton
                                        size="small"
                                        onClick={deleteLocationImage}
                                        disabled={saving}
                                        title={t('delete')}
                                    >
                                        <ClearIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            )}
                            <TrolleyShelterLocationImageViewer imagePath={module.imagePath || null} />
                        </>
                    )}
                </Grid>
            )}
        </Grid>
    );
};

export default TrolleyShelterLocationImageEditor;
